/**
 * Track 57P — eBA tenant lifecycle panel (inside the client Provisioning tab).
 *
 * Operator surface for the managed lifecycle: Draft → Provisioning → Active
 * ⇄ Suspended → Decommission scheduled → Quarantined → Deleted, with explicit
 * failed states. Every mutation:
 *   - is offered only when the server lists it in `available_actions`
 *     (roles + state + protection are decided server-side);
 *   - needs a fresh authenticator (TOTP) step-up, cached for its 5-minute life;
 *   - carries an idempotency key minted when the dialog opens, so a double
 *     click or a retried request can never act twice;
 *   - carries `expected_version`, so a stale screen gets a clear 409.
 * Requests only RECORD durable operations; the privileged worker performs
 * them. The panel therefore shows progress, the failed step, the error code
 * and the correlation id, and never claims success the server did not report.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleLogin } from "@react-oauth/google";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Warning, ArrowClockwise, Plus, CaretDown, CaretRight, Lock } from "@phosphor-icons/react";
import {
  ACTION_ENDPOINTS, ACTION_HELP, ACTION_LABELS, BASE_CURRENCY_DEFAULT, BASE_CURRENCY_HELP, DRAFT_DEFAULT_LICENCES,
  OPERATION_LABELS, STEP_LABELS, SYNC_LABELS, TONE_CLASSES, baseCurrencyChoice, baseCurrencyLabel,
  baseCurrencyOptionLabel, defaultDraftModules, describeLifecycleError, needsTypedConfirmation, newIdempotencyKey, stateTone,
  stepUpIsFresh, typedConfirmationMatches, workerHealthMessage, zeroLicenceWarning,
} from "@/lib/tenantLifecycle";

const card = "border border-[#E5E5E5] p-5 bg-[#FAFAFA] mb-4";
const label = "text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252]";
const sectionTitle = "text-sm font-bold uppercase tracking-[0.1em] text-[#0A0A0A] mb-3";
const POLL_MS = 5000;

function scope(clientId) {
  if (!clientId) throw new Error("Missing client context — refusing an unscoped lifecycle request.");
  return { params: { client_id: clientId } };
}

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function Row({ name, children }) {
  return (
    <div className="flex items-start gap-2 py-0.5">
      <div className={`${label} w-[180px] flex-shrink-0 py-1`}>{name}</div>
      <div className="text-sm py-1 break-all">{children ?? <span className="text-[#A3A3A3] italic">—</span>}</div>
    </div>
  );
}

function StateBadge({ lc }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold border ${TONE_CLASSES[stateTone(lc)]}`}
          data-testid="lifecycle-state">
      {lc.is_in_progress && <ArrowClockwise size={12} className="animate-spin" />}
      {lc.state_label}
    </span>
  );
}

// ── Action dialog ──────────────────────────────────────────────────────

function ActionDialog({ request, clientId, stepUp, setStepUp, catalog, exempt, onClose, onDone }) {
  const { mode, action, lifecycle, operation } = request;
  const keyRef = useRef(newIdempotencyKey());
  const [code, setCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [confirmEnv, setConfirmEnv] = useState("");
  const [form, setForm] = useState(() => initialForm(mode, action, lifecycle));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  // Development onboarding needs no authenticator code (server rule, Michael 2026-10-22); every other
  // action still does. The server decides — this only avoids asking for a code it will not check.
  const exemptEnvs = exempt?.environments || [];
  const exemptActions = exempt?.actions || [];
  const stepUpRequired = mode === "draft"
    ? !(exemptActions.includes("create_draft") && exemptEnvs.includes((form.environment_name || "").trim().toLowerCase()))
    : mode === "retry"
      ? !(exemptActions.includes(`retry_${operation.kind}`) && exemptEnvs.includes(lifecycle?.environment_name))
      : mode === "cancel_op"
        ? true
        : !(lifecycle?.available_actions || []).some((a) => a.action === action && a.step_up_required === false);
  const needStepUp = stepUpRequired && !stepUpIsFresh(stepUp);
  // Pre-tick once for a new draft: the default modules (Requisitions, Purchase Orders) with their functions,
  // and — for Native Accounting — its (module-less) accounting functions.
  const draftPreset = useRef(false);
  useEffect(() => {
    if (mode !== "draft" || draftPreset.current || !catalog?.functions || !catalog?.modules) return;
    draftPreset.current = true;
    setForm((f) => {
      const modules = Array.from(new Set([...(f.modules || []), ...defaultDraftModules(catalog.modules)]));
      let functions = withModuleFunctions(f.modules || [], modules, f.functions || [], catalog.functions);
      if (f.accounting_mode === "NATIVE") {
        const acct = catalog.functions.filter((x) => x.key.startsWith("fn_accounting_") && x.selectable).map((x) => x.key);
        functions = Array.from(new Set([...functions, ...acct]));
      }
      return { ...f, modules, functions };
    });
  }, [mode, catalog]);
  const needConfirm = mode === "action" && needsTypedConfirmation(action);
  const confirmOk = !needConfirm || typedConfirmationMatches(lifecycle, confirmCode, confirmEnv);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));

  const title = mode === "draft" ? "New eBA tenant draft"
    : mode === "retry" ? `Retry ${OPERATION_LABELS[operation.kind] || operation.kind}`
      : mode === "cancel_op" ? `Cancel ${OPERATION_LABELS[operation.kind] || operation.kind}`
        : ACTION_LABELS[action];
  const help = mode === "draft"
    ? "Reserves the tenant ID, company code and dedicated database name. Nothing is created in eBA or Azure until you Provision."
    : mode === "retry" ? "Re-runs the operation from the failed step. Completed steps are not repeated."
      : mode === "cancel_op" ? "Only allowed when the operation has not changed any external system."
        : ACTION_HELP[action];

  const submit = async () => {
    setPending(true);
    setError(null);
    try {
      let token = stepUpRequired ? stepUp?.token : undefined;
      if (needStepUp) {
        const { data } = await api.post("/eba2/lifecycle/step-up", { code: code.trim() });
        token = data.step_up_token;
        setStepUp({ token, expiresAt: data.expires_at });
      }
      const base = { idempotency_key: keyRef.current, step_up_token: token, expected_version: lifecycle?.version };
      let res;
      if (mode === "draft") {
        res = await api.post(`/eba2/lifecycle/clients/${clientId}/drafts`, draftPayload(form, base));
      } else if (mode === "retry" || mode === "cancel_op") {
        const verb = mode === "retry" ? "retry" : "cancel";
        res = await api.post(`/eba2/lifecycle/${lifecycle.id}/operations/${operation.id}/${verb}`,
          { step_up_token: token, expected_version: lifecycle.version }, scope(clientId));
      } else {
        const [method, path] = ACTION_ENDPOINTS[action];
        const body = { ...base, ...actionPayload(action, form), confirm_company_code: confirmCode, confirm_environment: confirmEnv };
        res = await api[method](`/eba2/lifecycle/${lifecycle.id}/${path}`, body, scope(clientId));
      }
      const data = res?.data || {};
      const op = data.operation;
      if (op && op.status === "pending") {
        toast.success(`${title}: request recorded. The lifecycle worker will run it — progress appears below.`);
      } else if (data.replayed) {
        toast.info(`${title}: this request was already recorded — nothing was done twice.`);
      } else {
        toast.success(`${title}: saved.`);
      }
      onDone();
    } catch (e) {
      const d = describeLifecycleError(e, `${title} failed.`);
      if (d.code === "step_up_required" || d.code === "step_up_invalid_code" || d.code === "step_up_code_reused") {
        setStepUp(null);
      }
      setError(d);
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !pending) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lifecycle-action-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{help}</DialogDescription>
        </DialogHeader>

        {mode === "draft" && <DraftFields form={form} set={set} catalog={catalog} />}
        {mode === "action" && <ActionFields action={action} form={form} set={set} catalog={catalog} lifecycle={lifecycle} />}

        {needConfirm && (
          <div className="border border-red-300 bg-red-50 p-3 space-y-2">
            <div className="text-xs font-bold text-red-800 flex items-center gap-1"><Warning size={14} /> Type to confirm</div>
            {action === "remove_tenant" && (
              <p className="text-xs text-red-900" data-testid="remove-tenant-identity">
                You are removing <strong>{lifecycle.legal_name || lifecycle.display_name}</strong> — tenant{" "}
                <code>{lifecycle.tenant_code}</code>, company <code>{lifecycle.company_code}</code>, database{" "}
                <code>{lifecycle.database_name}</code>. No other tenant is affected.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className={label}>Company code ({lifecycle.company_code})</Label>
                <Input value={confirmCode} onChange={(e) => setConfirmCode(e.target.value)} data-testid="confirm-company-code" />
              </div>
              <div>
                <Label className={label}>Environment ({lifecycle.environment_name})</Label>
                <Input value={confirmEnv} onChange={(e) => setConfirmEnv(e.target.value)} data-testid="confirm-environment" />
              </div>
            </div>
          </div>
        )}

        {!stepUpRequired ? (
          <p className="text-[11px] text-[#525252] flex items-center gap-1" data-testid="step-up-not-required">
            <ShieldCheck size={12} /> No authenticator code needed: onboarding an eBA Development tenant. This is still
            recorded in the audit timeline under your Google sign-in.
          </p>
        ) : needStepUp ? (
          <div>
            <Label className={label}>Authenticator code</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   inputMode="numeric" autoComplete="one-time-code" placeholder="6-digit code" data-testid="step-up-code" />
            <p className="text-[11px] text-[#737373] mt-1">
              Lifecycle actions need a fresh code from your authenticator app. It stays valid for 5 minutes.
            </p>
          </div>
        ) : (
          <p className="text-[11px] text-green-700 flex items-center gap-1"><ShieldCheck size={12} /> Authenticator confirmed for this session.</p>
        )}

        {error && (
          <div className="border border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert" data-testid="lifecycle-dialog-error">
            <div className="font-bold">Not done.</div>
            <div>{error.message}</div>
            {error.reference && <div className="text-xs mt-1">Reference: {error.reference}</div>}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>Close</Button>
          <Button onClick={submit} disabled={pending || !confirmOk || (needStepUp && code.length !== 6)}
                  variant={needConfirm || action === "suspend" ? "destructive" : "default"} data-testid="lifecycle-submit">
            {pending ? "Recording…" : title}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialForm(mode, action, lc) {
  if (mode === "draft") {
    return { tenant_code: "", company_code: "", environment_name: "dev", legal_name: "", display_name: "",
             address_line1: "", address_line2: "", address_city: "", address_region: "", address_postal_code: "",
             address_country: "", initial_admin_user_id: "", initial_admin_first_name: "", initial_admin_last_name: "",
             initial_admin_email: "", accounting_mode: "NATIVE",
             initial_data_mode: "empty", base_currency_code: BASE_CURRENCY_DEFAULT, ...DRAFT_DEFAULT_LICENCES, modules: [], functions: [],
             contact_name: "", contact_email: "", contact_phone: "" };
  }
  if (action === "update_entitlements" && lc) {
    return { licensed_main_users: lc.entitlements.licensed_main_users, licensed_requesters: lc.entitlements.licensed_requesters,
             modules: [...lc.entitlements.modules], functions: [...lc.entitlements.functions] };
  }
  if (action === "set_legal_hold" && lc) return { legal_hold: !lc.legal_hold, reason: "" };
  if (action === "schedule_decommission") {
    const d = new Date(Date.now() + 7 * 86400000);
    return { scheduled_for: d.toISOString().slice(0, 16), reason: "" };
  }
  return { reason: "" };
}

function draftPayload(f, base) {
  const contacts = f.contact_name.trim()
    ? [{ contact_role: "primary", name: f.contact_name, email: f.contact_email || null, phone: f.contact_phone || null }]
    : [];
  const { contact_name, contact_email, contact_phone, ...rest } = f;
  return { ...rest, ...base, contacts, licensed_main_users: Number(f.licensed_main_users) || 0,
           licensed_requesters: Number(f.licensed_requesters) || 0 };
}

function actionPayload(action, f) {
  switch (action) {
    case "update_entitlements":
      return { licensed_main_users: Number(f.licensed_main_users) || 0, licensed_requesters: Number(f.licensed_requesters) || 0,
               modules: f.modules, functions: f.functions };
    case "schedule_decommission":
      return { scheduled_for: new Date(f.scheduled_for).toISOString(), reason: f.reason };
    case "set_legal_hold":
      return { legal_hold: f.legal_hold, reason: f.reason };
    case "reissue_invitation":
      return { reason: f.reason };
    default:
      return f.reason !== undefined ? { reason: f.reason || null } : {};
  }
}

function Field({ name, value, onChange, ...rest }) {
  return (
    <div>
      <Label className={label}>{name}</Label>
      <Input value={value} onChange={onChange} {...rest} />
    </div>
  );
}

// Ruling 7: items are eBA's canonical capability keys. A function eBA does
// not enforce is never offered as a supported selection; one the tenant
// already has stays visible (and can be removed) with its current status.
function SelectionList({ title, items, selected, onChange }) {
  const toggle = (code) => onChange(selected.includes(code) ? selected.filter((c) => c !== code) : [...selected, code]);
  const known = new Set((items || []).map((m) => m.key));
  const withdrawn = selected.filter((k) => items && !known.has(k));
  return (
    <div>
      <div className={label}>{title} — {selected.length} selected</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1 max-h-48 overflow-y-auto border border-[#E5E5E5] p-2 bg-white">
        {(items || []).filter((m) => m.selectable || selected.includes(m.key)).map((m) => (
          <label key={m.key} className="text-xs flex items-center gap-2">
            <input type="checkbox" checked={selected.includes(m.key)} onChange={() => toggle(m.key)}
                   disabled={!m.selectable && !selected.includes(m.key)} />
            <span>{m.display_name} <code className="text-[10px] text-[#737373]">{m.key}</code></span>
            {!m.selectable && (
              <span className="text-[10px] text-amber-800 border border-amber-300 px-1" title={m.description || ""}>
                not supported by eBA
              </span>
            )}
            {m.admin_enforcement === "not-enforced" && (
              <span className="text-[10px] text-[#737373] border border-[#D4D4D4] px-1" title="eBA's only server check for this function has an administrator bypass.">
                admins bypass
              </span>
            )}
          </label>
        ))}
        {withdrawn.map((k) => (
          <label key={k} className="text-xs flex items-center gap-2 text-red-800">
            <input type="checkbox" checked onChange={() => toggle(k)} />
            <code>{k}</code> <span className="text-[10px] border border-red-300 px-1">withdrawn by eBA — remove to save</span>
          </label>
        ))}
        {!items && <span className="text-xs text-[#A3A3A3]">Loading catalogue…</span>}
        {items && items.length === 0 && <span className="text-xs text-[#A3A3A3]">None published by eBA.</span>}
      </div>
    </div>
  );
}

function SupportList({ entries }) {
  if (!entries || entries.length === 0) return "None selected";
  return (
    <span className="flex flex-wrap gap-1">
      {entries.map((e) => (
        <span key={e.key} className={`text-[11px] border px-1 ${e.status === "supported" ? "border-[#D4D4D4]"
          : e.status === "not_enforced" ? "border-amber-300 text-amber-900" : "border-red-300 text-red-800"}`}
              title={e.status === "supported" ? "Enforced by eBA" : e.status === "not_enforced"
                ? "eBA records this key but does not enforce it" : "eBA no longer lists this key"}>
          {e.display_name}{e.status === "not_enforced" ? " (not enforced)" : e.status === "withdrawn" ? " (withdrawn)" : ""}
        </span>
      ))}
    </span>
  );
}

// Ticking a module adds its selectable functions; unticking removes them (2026-10-22 defect:
// a module without its functions is hidden by eBA's entitlement ceiling).
function withModuleFunctions(prevModules, nextModules, functions, catalogFunctions) {
  const fnsOf = (m) => (catalogFunctions || []).filter((f) => f.module_key === m && f.selectable).map((f) => f.key);
  const added = nextModules.filter((m) => !prevModules.includes(m));
  const removed = prevModules.filter((m) => !nextModules.includes(m));
  let out = functions.filter((f) => !removed.some((m) => fnsOf(m).includes(f)));
  added.forEach((m) => fnsOf(m).forEach((f) => { if (!out.includes(f)) out = [...out, f]; }));
  return out;
}

function EntitlementFields({ form, set, catalog }) {
  return (
    <div className="space-y-3">
      {catalog.available === false && (
        <p className="text-xs border border-amber-300 bg-amber-50 text-amber-900 p-2" data-testid="capability-catalog-unavailable">
          eBA's capability catalogue has not been synchronised yet, so modules and functions cannot be selected.
          Licence quantities can still be saved.
        </p>
      )}
      <div className="grid grid-cols-2 gap-2">
        <Field name="Licensed main users" type="number" min={0} value={form.licensed_main_users} onChange={set("licensed_main_users")} />
        <Field name="Licensed requesters" type="number" min={0} value={form.licensed_requesters} onChange={set("licensed_requesters")} />
      </div>
      {zeroLicenceWarning(form.licensed_main_users, form.licensed_requesters) && (
        <p className="text-xs border border-amber-300 bg-amber-50 text-amber-900 p-2 flex items-start gap-1" role="status"
           data-testid="zero-licence-warning">
          <Warning size={14} className="mt-0.5 shrink-0" />
          {zeroLicenceWarning(form.licensed_main_users, form.licensed_requesters)}
        </p>
      )}
      <SelectionList title="Modules" items={catalog.modules} selected={form.modules}
                     onChange={(next) => {
                       set("functions")(withModuleFunctions(form.modules, next, form.functions, catalog.functions));
                       set("modules")(next);
                     }} />
      <p className="text-[11px] text-[#737373]">
        Selecting a module also selects its functions — eBA shows a module's menus only when at least one of its
        functions is purchased. You can untick individual functions.
      </p>
      <SelectionList title="Functions" items={catalog.functions} selected={form.functions} onChange={set("functions")} />
    </div>
  );
}

// Empty (default) or test data. Fixed once provisioning is requested; test data only when eBA offers a dataset.
function InitialDataField({ form, set, catalog }) {
  const offer = catalog.initial_data || { modes: [{ mode: "empty", label: "Empty tenant", available: true }] };
  return (
    <fieldset data-testid="initial-data-choice">
      <Label className={label}>Initial data</Label>
      {offer.modes.map((m) => (
        <label key={m.mode} className={`flex items-start gap-2 text-sm ${m.available ? "" : "text-[#A3A3A3]"}`}>
          <input type="radio" name="initial_data_mode" value={m.mode} checked={form.initial_data_mode === m.mode}
                 disabled={!m.available} onChange={set("initial_data_mode")} className="mt-1" />
          <span>
            {m.label}
            <span className="block text-[11px] text-[#737373]">
              {m.mode === "empty"
                ? "No business or demo records — essential system settings and one administrator, activated through the invitation."
                : m.available
                  ? `eBA's synthetic test dataset ${offer.test_data_dataset_version} (at least 100 records per operational table, clearly marked as test data; no emails or supplier transmissions).`
                  : "Not offered by eBA yet."}
            </span>
          </span>
        </label>
      ))}
      <p className="text-[11px] text-[#737373]">You can change this while the tenant is a draft. It is fixed once provisioning is requested.</p>
    </fieldset>
  );
}

// ISO 4217 reference currency, default USD. Selectable only when eBA's catalogue advertises it;
// otherwise shown as eBA's default and nothing is sent.
function BaseCurrencyField({ form, set, catalog }) {
  const choice = baseCurrencyChoice(catalog);
  const value = choice.supported ? form.base_currency_code || BASE_CURRENCY_DEFAULT : BASE_CURRENCY_DEFAULT;
  return (
    <div>
      <Label className={label} htmlFor="draft-base-currency">Base currency</Label>
      <select id="draft-base-currency" data-testid="draft-base-currency"
              className="block w-full border border-[#E5E5E5] h-9 px-2 text-sm bg-white disabled:text-[#737373]"
              value={value} onChange={set("base_currency_code")} disabled={!choice.supported}>
        {choice.options.map((o) => (
          <option key={o.code} value={o.code}>{baseCurrencyOptionLabel(o, choice.supported)}</option>
        ))}
      </select>
      <p className="text-[11px] text-[#737373] mt-1">{BASE_CURRENCY_HELP}</p>
      {!choice.supported && (
        <p className="text-[11px] text-[#737373]" data-testid="base-currency-not-offered">
          eBA does not accept a base currency at provisioning yet, so the tenant is created in USD.
        </p>
      )}
      {choice.supported && form.initial_data_mode === "test_data" && value !== BASE_CURRENCY_DEFAULT && (
        <p className="text-[11px] text-amber-900 border border-amber-300 bg-amber-50 p-1 mt-1" role="status">
          eBA's test dataset is USD-based: a tenant with test data must use USD. Choose USD or an empty tenant.
        </p>
      )}
    </div>
  );
}

function DraftFields({ form, set, catalog }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        <Field name="Tenant ID" value={form.tenant_code} onChange={set("tenant_code")} placeholder="acme-prod" data-testid="draft-tenant-id" />
        <Field name="Company code" value={form.company_code} onChange={set("company_code")} placeholder="ACME" />
        <Field name="Environment" value={form.environment_name} onChange={set("environment_name")} placeholder="dev / uat / prod" />
      </div>
      <p className="text-[11px] text-[#737373]">
        The dedicated database is derived as <code>eba2_&lt;company code&gt;_&lt;environment&gt;</code>. Company codes may not contain a period.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Field name="Legal name" value={form.legal_name} onChange={set("legal_name")} />
        <Field name="Display name" value={form.display_name} onChange={set("display_name")} />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field name="Address line 1 (required)" value={form.address_line1} onChange={set("address_line1")} />
        <Field name="Address line 2" value={form.address_line2} onChange={set("address_line2")} />
        <Field name="City (required)" value={form.address_city} onChange={set("address_city")} />
        <Field name="Region / county" value={form.address_region} onChange={set("address_region")} />
        <Field name="Postal code" value={form.address_postal_code} onChange={set("address_postal_code")} />
        <Field name="Country (required, ISO e.g. GB)" value={form.address_country} onChange={set("address_country")} maxLength={2} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field name="Primary contact" value={form.contact_name} onChange={set("contact_name")} />
        <Field name="Contact email" value={form.contact_email} onChange={set("contact_email")} />
        <Field name="Contact phone" value={form.contact_phone} onChange={set("contact_phone")} />
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="initial-admin-fields">
        <Field name="Administrator User ID (business ID in eBA)" value={form.initial_admin_user_id} onChange={set("initial_admin_user_id")} placeholder="e.g. mpetter" />
        <Field name="Administrator email (sign-in & invitation)" type="email" value={form.initial_admin_email} onChange={set("initial_admin_email")} />
        <Field name="Administrator first name" value={form.initial_admin_first_name} onChange={set("initial_admin_first_name")} />
        <Field name="Administrator last name" value={form.initial_admin_last_name} onChange={set("initial_admin_last_name")} />
      </div>
      <p className="text-[11px] text-[#737373]">
        All four are required. The User ID is the person's business identifier in eBA; the email is where the secure
        invitation is sent and what they sign in with. The administrator gets the "All Access" role automatically,
        limited to the modules and functions purchased below.
      </p>
      <div>
        <Label className={label}>Accounting mode</Label>
        <select className="block w-full border border-[#E5E5E5] h-9 px-2 text-sm bg-white" value={form.accounting_mode}
                onChange={set("accounting_mode")}>
          <option value="NATIVE">Native (default for new clients)</option>
          <option value="LEGACY">Legacy (explicit)</option>
        </select>
        <p className="text-[11px] text-[#737373] mt-1">
          Native Accounting needs the accounting functions (at least "Accounting configuration"); they are pre-selected
          below so the administrator can set up Accounting.
        </p>
      </div>
      <BaseCurrencyField form={form} set={set} catalog={catalog} />
      <InitialDataField form={form} set={set} catalog={catalog} />
      <EntitlementFields form={form} set={set} catalog={catalog} />
    </div>
  );
}

function ActionFields({ action, form, set, catalog, lifecycle }) {
  if (action === "update_entitlements") {
    return (
      <div className="space-y-2">
        {lifecycle.state !== "draft" && (
          <p className="text-[11px] text-[#737373]">The change is saved now and synced to eBA by the lifecycle worker.</p>
        )}
        <EntitlementFields form={form} set={set} catalog={catalog} />
      </div>
    );
  }
  if (action === "schedule_decommission") {
    return (
      <div className="space-y-2">
        <Field name="Quarantine on" type="datetime-local" value={form.scheduled_for} onChange={set("scheduled_for")} />
        <Field name="Reason (required)" value={form.reason} onChange={set("reason")} />
      </div>
    );
  }
  if (action === "set_legal_hold") {
    return (
      <div className="space-y-2">
        <p className="text-sm">{form.legal_hold ? "Place this tenant under legal hold." : "Release the legal hold."}</p>
        <Field name={form.legal_hold ? "Reason (required)" : "Note"} value={form.reason} onChange={set("reason")} />
      </div>
    );
  }
  if (action === "reissue_invitation") {
    return (
      <div className="space-y-2">
        <p className="text-sm">Recipient: {lifecycle.initial_admin_name} &lt;{lifecycle.initial_admin_email}&gt;</p>
        <Field name="Reason (required)" value={form.reason} onChange={set("reason")} />
      </div>
    );
  }
  if (["suspend", "resume", "cancel_decommission", "restore"].includes(action)) {
    return <Field name="Reason / note" value={form.reason} onChange={set("reason")} />;
  }
  return null;
}

// ── First-time authenticator enrolment (lifecycle step-up) ─────────────

function AuthenticatorEnrolment({ onDone }) {
  const [open, setOpen] = useState(false);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [reauth, setReauth] = useState(false);

  // Starting needs a fresh Google sign-in of this same account (server-checked: same email, under 10 minutes old).
  const start = async (googleCredential) => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.post("/eba2/lifecycle/authenticator/enrol", { google_credential: googleCredential });
      setSetup(data); setOpen(true); setReauth(false);
    } catch (e) {
      setError(describeLifecycleError(e, "Could not start authenticator setup."));
    } finally { setBusy(false); }
  };
  const confirm = async () => {
    setBusy(true); setError(null);
    try {
      await api.post("/eba2/lifecycle/authenticator/confirm", { code });
      toast.success("Authenticator enrolled. Lifecycle actions will ask for its 6-digit code.");
      setOpen(false); setSetup(null); onDone();
    } catch (e) {
      setError(describeLifecycleError(e, "That code was not accepted."));
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-amber-300 bg-amber-50 text-amber-900 text-xs p-3 mb-3" data-testid="authenticator-enrolment">
      <b>Set up an authenticator for protected lifecycle actions.</b> Suspending, decommissioning and other protected
      changes are confirmed with a fresh 6-digit code; onboarding eBA Development tenants does not need one. This does
      not change how you sign in.
      {!reauth && (
        <div className="mt-2"><Button size="sm" onClick={() => { setError(null); setReauth(true); }} disabled={busy}>Set up authenticator</Button></div>
      )}
      {reauth && (
        <div className="mt-2" data-testid="authenticator-reauth">
          <div className="mb-1">First confirm it is you: sign in with your own @eyvo.com Google account.</div>
          <GoogleLogin
            onSuccess={(cred) => start(cred.credential)}
            onError={() => setError({ message: "Google sign-in did not complete. Allow pop-ups and try again." })}
            size="medium" text="continue_with" shape="rectangular"
          />
          <Button size="sm" variant="ghost" className="mt-1" onClick={() => setReauth(false)} disabled={busy}>Cancel</Button>
        </div>
      )}
      {error && !open && <div className="text-red-700 mt-1">{error.message}</div>}
      {open && setup && (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) setOpen(false); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Set up your authenticator</DialogTitle>
              <DialogDescription>
                Scan the QR code with Microsoft Authenticator, Google Authenticator or another TOTP app, or enter the key
                manually. The key is shown only now.
              </DialogDescription>
            </DialogHeader>
            <img alt="Authenticator QR code" className="mx-auto" src={`data:image/png;base64,${setup.qr_code_png_base64}`} />
            <div className="text-center text-xs font-mono break-all">{setup.secret_key}</div>
            <Label className={label}>Code from your app</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                   inputMode="numeric" autoComplete="one-time-code" data-testid="authenticator-confirm-code" />
            {error && <div className="text-xs text-red-700">{error.message}</div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
              <Button onClick={confirm} disabled={busy || code.length !== 6}>Confirm</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Lifecycle card ─────────────────────────────────────────────────────

function OperationRow({ op, lc, canControl, onRetry, onCancel }) {
  const failed = op.status === "failed";
  return (
    <div className={`border p-2 mb-2 text-xs ${failed ? "border-red-300 bg-red-50" : "border-[#E5E5E5] bg-white"}`}>
      <div className="flex justify-between gap-2">
        <span className="font-bold">{OPERATION_LABELS[op.kind] || op.kind} — {op.status}</span>
        <span className="text-[#737373]">{fmt(op.requested_at)} · {op.requested_by}</span>
      </div>
      <div className="text-[#737373]">Correlation ID: <code>{op.correlation_id}</code>{op.attempt_count ? ` · attempt ${op.attempt_count}/${op.max_attempts}` : ""}</div>
      {op.steps?.length > 0 && (
        <ol className="mt-1 ml-4 list-decimal">
          {op.steps.filter((s) => s.status !== "intent").map((s, i) => (
            <li key={i} className={s.status === "failed" ? "text-red-700" : ""}>
              {STEP_LABELS[s.step] || s.step}: {s.status}{s.code ? ` (${s.code})` : ""}
            </li>
          ))}
        </ol>
      )}
      {op.status === "pending" && op.last_error_code && (
        <div className="text-amber-800 mt-1">Temporary failure ({op.last_error_code}); automatic retry at {fmt(op.next_attempt_at)}.</div>
      )}
      {failed && (
        <div className="mt-1 text-red-800">
          <div><b>{op.last_error_code}</b>: {op.last_error_message}</div>
          {canControl && lc.last_operation_id === op.id && (
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={() => onRetry(op)} data-testid="retry-operation">Retry</Button>
              {op.can_cancel && <Button size="sm" variant="outline" onClick={() => onCancel(op)}>Cancel</Button>}
            </div>
          )}
        </div>
      )}
      {op.status === "pending" && op.can_cancel && canControl && (
        <Button size="sm" variant="outline" className="mt-2" onClick={() => onCancel(op)}>Cancel (not started)</Button>
      )}
    </div>
  );
}

function LifecycleCard({ lc, clientId, roles, onOpen, refreshKey }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const loadDetail = useCallback(async () => {
    try {
      const { data } = await api.get(`/eba2/lifecycle/${lc.id}`, scope(clientId));
      setDetail(data);
      setDetailError(null);
    } catch (e) {
      setDetailError(describeLifecycleError(e, "Could not load the lifecycle history."));
    }
  }, [lc.id, clientId]);

  useEffect(() => { if (open) loadDetail(); }, [open, refreshKey, loadDetail]);

  const ent = lc.entitlements;
  const canControl = roles.length > 0;
  const actions = lc.available_actions || [];

  return (
    <div className={card} data-testid="lifecycle-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-black">{lc.display_name}</span>
            <StateBadge lc={lc} />
            {lc.is_protected && <span className="text-[10px] font-bold border border-[#0A0A0A] px-1.5 py-0.5 flex items-center gap-1"><Lock size={10} /> PROTECTED</span>}
            {lc.legal_hold && <span className="text-[10px] font-bold border border-amber-500 text-amber-800 px-1.5 py-0.5">LEGAL HOLD</span>}
          </div>
          <div className="text-xs text-[#737373]">{lc.legal_name}</div>
        </div>
        <div className="flex flex-wrap gap-1 justify-end">
          {actions.map((a) => (
            <Button key={a.action} size="sm" variant={a.destructive ? "destructive" : "outline"}
                    onClick={() => onOpen({ mode: "action", action: a.action, lifecycle: lc })} data-testid={`action-${a.action}`}>
              {ACTION_LABELS[a.action] || a.action}
            </Button>
          ))}
        </div>
      </div>

      {lc.is_failed && (
        <div className="border border-red-300 bg-red-50 p-3 mt-3 text-sm text-red-800" role="alert">
          <b>{lc.state_label}.</b> {lc.last_error_message || "The last operation failed."}
          {lc.last_error_code && <span className="text-xs"> ({lc.last_error_code})</span>}
          <div className="text-xs mt-1">Open the history below to Retry, or to Cancel when that is safe.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-3">
        <div>
          <Row name="Tenant ID"><code>{lc.tenant_code}</code></Row>
          <Row name="Company code"><code>{lc.company_code}</code></Row>
          <Row name="Environment">{lc.environment_name}</Row>
          <Row name="Dedicated database"><code>{lc.database_name}</code></Row>
          <Row name="Secret reference">{lc.secret_reference_configured ? "Configured (value never shown)" : "Not created yet"}</Row>
          <Row name="Accounting mode">{lc.accounting_mode === "LEGACY" ? "Legacy" : "Native"}</Row>
          <Row name="Base currency">
            <span data-testid="tenant-base-currency">{baseCurrencyLabel(lc)}</span>
            {lc.base_currency?.fixed ? " (fixed here; changed in eBA: System administration › Set base currency)"
              : " (can change while Draft)"}
          </Row>
          <Row name="Initial data">
            {lc.initial_data?.label || "Empty tenant"}
            {lc.initial_data?.fixed ? " (fixed)" : " (can change while Draft)"}
            {lc.initial_data?.status === "eba_unverified" && (
              <span className="block text-[11px] text-amber-800" data-testid="initial-data-unverified">
                Nothing was seeded. eBA does not offer initial-data verification yet, so emptiness was not
                confirmed by eBA.
              </span>
            )}
            {lc.initial_data?.status === "completed" && (
              <span className="block text-[11px] text-[#737373]" data-testid="initial-data-result">
                Confirmed by eBA{lc.initial_data.dataset_version ? ` — dataset ${lc.initial_data.dataset_version}` : ""}:{" "}
                {Object.values(lc.initial_data.counts || {}).reduce((a, b) => a + Number(b || 0), 0)} records across{" "}
                {Object.keys(lc.initial_data.counts || {}).length} tables
              </span>
            )}
          </Row>
          <Row name="Initial administrator">
            {lc.initial_admin_name} &lt;{lc.initial_admin_email}&gt;
            <span className="block text-[11px] text-[#737373]">
              User ID <code>{lc.initial_admin_user_id || "— (not recorded)"}</code> · first name {lc.initial_admin_first_name || "—"} · last name {lc.initial_admin_last_name || "—"}
            </span>
          </Row>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] mt-1">Requested in Pulse</div>
          <Row name="Licensed users (commercial)">{ent.licensed_main_users} main users · {ent.licensed_requesters} requesters</Row>
          <Row name="eBA licence ceiling">
            <span title={ent.licence_enforcement_note}>
              {ent.eba_licensed_user_limit} licensed non-administrator users (Main Users)
              <span className="block text-[11px] text-amber-800">
                Requesters ({ent.licensed_requesters}): not enforced by eBA — commercial record only.
              </span>
            </span>
          </Row>
          <Row name={`Modules (${ent.module_count})`}><SupportList entries={ent.module_support} /></Row>
          <Row name={`Functions (${ent.function_count})`}><SupportList entries={ent.function_support} /></Row>
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#525252] mt-3">Applied in eBA</div>
          <Row name="Entitlements">
            {lc.eba_entitlement_version != null
              ? <>eBA confirmed version {lc.eba_entitlement_version} (Pulse version {lc.entitlements_version})</>
              : "Not applied in eBA yet"}
            <span className="block text-[11px] text-[#737373]">{SYNC_LABELS[lc.entitlements_sync_status] || lc.entitlements_sync_status}</span>
          </Row>
          {lc.eba_sign_in_url && lc.state === "active" && (
            <Row name="Sign in">
              <a className="underline" href={lc.eba_sign_in_url} target="_blank" rel="noreferrer">{lc.eba_sign_in_url}</a>
              <span className="block text-[11px] text-[#737373]">Enter company code <code>{lc.company_code}</code> on the sign-in page.</span>
            </Row>
          )}
          {lc.eba_state && (
            <Row name="eBA reports">
              {lc.eba_state} · lifecycle v{lc.eba_lifecycle_version} · entitlements v{lc.eba_entitlement_version}
              {lc.eba_licensed_users != null && <> · {lc.eba_licensed_users} licensed users in use</>}
              {lc.eba_over_limit && <span className="text-red-700 font-bold"> · OVER LIMIT</span>}
              <span className="block text-[11px] text-[#737373]">checked {fmt(lc.eba_status_checked_at)}</span>
            </Row>
          )}
          {lc.admin_invitation_delivery && (
            <Row name="Admin invitation">
              {lc.admin_invitation_delivery === "sent" ? "Sent by eBA"
                : lc.admin_invitation_delivery === "already-invited" ? "Already invited (no second invitation sent)"
                  : `Created, but email delivery reported: ${lc.admin_invitation_delivery}`}
            </Row>
          )}
          {lc.eba_finalized_at && (
            <Row name="eBA finalization">
              <span className="text-red-800 font-bold">Finalized in eBA {fmt(lc.eba_finalized_at)}</span>
              <span className="block text-[11px] text-[#737373]">Routing removed; this tenant can never be resumed and its identifiers stay retired.</span>
            </Row>
          )}
          {lc.decommission_scheduled_for && <Row name="Quarantine scheduled">{fmt(lc.decommission_scheduled_for)} — {lc.decommission_reason}</Row>}
          {lc.quarantine_started_at && <Row name="Quarantined since">{fmt(lc.quarantine_started_at)}</Row>}
          {lc.state === "quarantined" && <Row name="Retention until">{lc.retention_until ? fmt(lc.retention_until) : "No retention policy configured — deletion is not possible"}</Row>}
          {lc.recovery_verified_at && (
            <Row name="Backup recovery-verified">
              {fmt(lc.recovery_verified_at)}
              <span className="block text-[11px] text-[#737373]">Restored to a scratch database, integrity-checked and compared. {lc.backup_reference}</span>
            </Row>
          )}
        </div>
      </div>

      <button className="text-xs font-bold mt-3 flex items-center gap-1" onClick={() => setOpen((o) => !o)} data-testid="lifecycle-history-toggle">
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />} Status timeline & operations
      </button>
      {open && (
        <div className="mt-2">
          {detailError && <div className="text-xs text-red-700">{detailError.message}{detailError.reference ? ` (Reference: ${detailError.reference})` : ""}</div>}
          {!detail && !detailError && <div className="text-xs text-[#A3A3A3]">Loading history…</div>}
          {detail?.deletion && (
            <div className="border border-[#E5E5E5] bg-white p-2 mb-2 text-xs">
              <b>Permanent deletion:</b> {detail.deletion.blockers.length === 0 ? "all guards pass." : "blocked —"}
              <ul className="ml-4 list-disc">{detail.deletion.messages.map((m) => <li key={m}>{m}</li>)}</ul>
              <div>Approvals: {detail.deletion.approvals} of {detail.deletion.required_approvals} required.</div>
            </div>
          )}
          {detail?.operations?.filter((o) => ["pending", "running", "failed"].includes(o.status) || o.steps?.length).map((op) => (
            <OperationRow key={op.id} op={op} lc={lc} canControl={canControl}
                          onRetry={(o) => onOpen({ mode: "retry", lifecycle: lc, operation: o })}
                          onCancel={(o) => onOpen({ mode: "cancel_op", lifecycle: lc, operation: o })} />
          ))}
          <div className={`${label} mt-3 mb-1`}>Audit timeline</div>
          <ol className="text-xs border-l border-[#D4D4D4] ml-1">
            {(detail?.events || []).map((ev) => (
              <li key={ev.id} className="pl-3 py-1">
                <span className="text-[#737373]">{fmt(ev.created_at)}</span> · <b>{ev.event_type.replace(/_/g, " ")}</b>
                {ev.from_state !== ev.to_state && <> · {ev.from_state || "—"} → {ev.to_state}</>}
                {" "}· {ev.actor}
                {ev.detail?.code && <span className="text-red-700"> · {ev.detail.code}</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────

export default function TenantLifecyclePanel({ clientId, onChanged }) {
  const [me, setMe] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [request, setRequest] = useState(null);
  const [stepUp, setStepUp] = useState(null);
  const [catalog, setCatalog] = useState({ modules: null, functions: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const [worker, setWorker] = useState(null);

  const load = useCallback(async () => {
    // Worker health is advisory: a failure here must not hide the tenants.
    api.get("/eba2/lifecycle/worker-status").then((r) => setWorker(r.data)).catch(() => setWorker(null));
    try {
      const [meRes, listRes] = await Promise.all([
        api.get("/eba2/lifecycle/me"),
        api.get(`/eba2/lifecycle/clients/${clientId}`, { params: showArchived ? { include_archived: true } : {} }),
      ]);
      setMe(meRes.data);
      setRows(listRes.data.lifecycles || []);
      setError(null);
    } catch (e) {
      setError(describeLifecycleError(e, "Could not load eBA tenant lifecycle."));
    }
    setRefreshKey((k) => k + 1);
  }, [clientId, showArchived]);

  useEffect(() => { if (clientId) load(); }, [clientId, load]);

  const anyInProgress = (rows || []).some((r) => r.is_in_progress || r.entitlements_sync_status === "pending");
  useEffect(() => {
    if (!anyInProgress) return undefined;
    const t = setTimeout(load, POLL_MS);
    return () => clearTimeout(t);
  }, [anyInProgress, load, refreshKey]);

  const openDialog = async (req) => {
    setRequest(req);
    if ((req.mode === "draft" || req.action === "update_entitlements") && !catalog.modules) {
      try {
        const { data } = await api.get("/eba2/lifecycle/capability-catalog");
        setCatalog({ available: data.available, modules: data.modules || [], functions: data.functions || [] });
      } catch (e) {
        toast.error(describeLifecycleError(e, "Could not load eBA's capability catalogue.").message);
        setCatalog({ available: false, modules: [], functions: [] });
      }
    }
  };

  const roles = me?.roles || [];
  const isAdministrator = roles.includes("TenantLifecycleAdministrator");

  return (
    <div className="mb-6" data-testid="tenant-lifecycle-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-black tracking-tight">eBA Tenant Lifecycle</h2>
          <p className="text-xs text-[#737373]">
            Provisioning, licences and modules, suspension, decommissioning and permanent deletion of this client's eBA tenants.
            Requests are recorded here and carried out by the secure lifecycle worker.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load}><ArrowClockwise size={14} className="mr-1" /> Refresh</Button>
          {isAdministrator && (
            <Button size="sm" onClick={() => openDialog({ mode: "draft" })} data-testid="new-lifecycle-draft">
              <Plus size={14} className="mr-1" /> New tenant draft
            </Button>
          )}
        </div>
      </div>

      {error && error.code === "lifecycle_schema_missing" && (
        <div className={card} data-testid="lifecycle-not-set-up">
          <div className={sectionTitle}>Not set up yet</div>
          <p className="text-xs text-[#525252]">{error.message}</p>
        </div>
      )}

      {error && error.code !== "lifecycle_schema_missing" && (
        <div className={`${card} border-red-300 bg-red-50 text-sm text-red-800`} role="alert">
          <b>Could not load tenant lifecycle.</b> {error.message}
          {error.reference && <div className="text-xs mt-1">Reference: {error.reference}</div>}
        </div>
      )}

      {!error && roles.length > 0 && me && !me.authenticator_enrolled && (
        <AuthenticatorEnrolment onDone={load} />
      )}

      {!error && workerHealthMessage(worker) && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 text-xs p-2 mb-3" data-testid="lifecycle-worker-health">
          {workerHealthMessage(worker)}
        </div>
      )}

      {!error && rows === null && <div className="text-sm text-[#A3A3A3]">Loading tenant lifecycle…</div>}

      {!error && rows && rows.length === 0 && (
        <div className={card} data-testid="lifecycle-empty">
          <div className={sectionTitle}>No managed eBA tenant yet</div>
          <p className="text-xs text-[#525252]">
            {isAdministrator
              ? "Create a tenant draft to reserve the tenant ID, company code and dedicated database. Nothing is created in eBA or Azure until you choose Provision."
              : "A Tenant Lifecycle Administrator can create and provision this client's eBA tenant."}
          </p>
        </div>
      )}

      {rows && (
        <label className="flex items-center gap-2 text-[11px] text-[#737373] mb-2" data-testid="show-archived">
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived test attempts (kept for audit; their identifiers stay reserved)
        </label>
      )}

      {(rows || []).map((lc) => (
        <LifecycleCard key={lc.id} lc={lc} clientId={clientId} roles={roles} onOpen={openDialog} refreshKey={refreshKey} />
      ))}

      {request && (
        <ActionDialog request={request} clientId={clientId} stepUp={stepUp} setStepUp={setStepUp} catalog={catalog}
                      exempt={{ environments: me?.step_up_exempt_environments, actions: me?.step_up_exempt_actions }}
                      onClose={() => setRequest(null)}
                      onDone={() => { setRequest(null); load(); if (onChanged) onChanged(); }} />
      )}
    </div>
  );
}
